/**
 * Agent-trust API client (Agents redesign, Phase 1): standing-agent roster, the sign-off
 * queue (approvals), and standing grants. Workspace-scoped routes; manual types (these
 * endpoints aren't in the generated OpenAPI contract yet — same pattern as agent-jobs).
 *
 * Note: `decideApproval` only RECORDS the user's decision server-side — it does not send
 * anything. Autonomous execution of an approved action is gated separately (not in P1).
 */
import { apiFetch } from "@/lib/apiClient";
import type { AutomationPreferences } from "@xavior/schema";

export type AgentApprovalStatus =
  | "pending"
  | "approved"
  | "edited"
  | "rejected"
  | "expired"
  | "executed"
  | "undone";
export type AgentActionClass = "free" | "consequential";
export type AgentAutonomyLevel = "suggest" | "confirm" | "auto";

export interface AgentApproval {
  id: string;
  runId: string;
  // Nullable: agent-less manual/ad-hoc durable runs (ADR-025) enqueue sign-offs
  // with no sub-agent — the API returns agentId: null for them (#4065).
  agentId: string | null;
  actionType: string;
  actionClass: AgentActionClass;
  preparedPayload: Record<string, unknown>;
  /** Optional user-only note about the draft — sibling of preparedPayload, never sent (#email-notes). */
  notes?: string;
  /** Optional one-line summary of the incoming email being replied to — user-only, never sent. */
  incomingSummary?: string;
  provenance: Record<string, unknown>;
  status: AgentApprovalStatus;
  noveltyFlags: string[];
  undoUntil?: string;
  decidedBy?: string;
  createdAt: string;
  decidedAt?: string;
}

export interface AgentGrant {
  id: string;
  agentId: string;
  actionType: string;
  scope: Record<string, unknown>;
  grantedBy?: string;
  grantedAt: string;
  revokedAt?: string;
  expiresAt?: string;
}

export interface StandingAgent {
  id: string;
  name: string;
  role?: string;
  status: string;
  enabled?: boolean;
  autonomyLevel?: AgentAutonomyLevel;
  triggerConfig?: Record<string, unknown>;
  guidelines?: string;
  actionAllowlist?: string[];
  /** Per-automation preferences (Phase 2) — typed by preset kind, server-validated. */
  preferences?: AutomationPreferences;
  lastRunAt?: string;
  lastReportSummary?: string;
  createdAt: string;
  updatedAt: string;
}

export type AgentConfigPatch = Partial<
  Pick<
    StandingAgent,
    | "autonomyLevel"
    | "enabled"
    | "role"
    | "triggerConfig"
    | "guidelines"
    | "actionAllowlist"
    | "preferences"
  >
>;

const ws = (workspaceId: string) => ({ "X-Workspace-Id": workspaceId });
const base = (workspaceId: string) => `/workspaces/${workspaceId}`;

// ── Roster ──────────────────────────────────────────────────────────────────
export const listStandingAgents = (token: string, workspaceId: string) =>
  apiFetch<{ items: StandingAgent[] }>(`${base(workspaceId)}/sub-agents`, {
    authToken: token,
    headers: ws(workspaceId),
  });

/** Create a sub-agent. Standing fields (autonomy/role/trigger) are set via a follow-up config PATCH. */
export const createStandingAgent = (
  token: string,
  workspaceId: string,
  body: {
    name: string;
    persona?: string;
    focusArea?: string;
    cronSchedule?: string;
  },
) =>
  apiFetch<StandingAgent>(`${base(workspaceId)}/sub-agents`, {
    method: "POST",
    authToken: token,
    headers: ws(workspaceId),
    body: JSON.stringify(body),
  });

export const updateAgentConfig = (
  token: string,
  workspaceId: string,
  agentId: string,
  patch: AgentConfigPatch,
) =>
  apiFetch<StandingAgent>(`${base(workspaceId)}/sub-agents/${agentId}/config`, {
    method: "PATCH",
    authToken: token,
    headers: ws(workspaceId),
    body: JSON.stringify(patch),
  });

// ── Per-automation preferences (Phase 2) ──────────────────────────────────────
/** Read one automation's stored preferences (the raw, server-validated object). */
export const getPreferences = (
  token: string,
  workspaceId: string,
  agentId: string,
) =>
  apiFetch<{ preferences: AutomationPreferences }>(
    `${base(workspaceId)}/sub-agents/${agentId}/preferences`,
    { authToken: token, headers: ws(workspaceId) },
  );

/** Replace one automation's preferences. The body IS the raw preferences object (server-validated). */
export const setPreferences = (
  token: string,
  workspaceId: string,
  agentId: string,
  preferences: AutomationPreferences,
) =>
  apiFetch<{ preferences: AutomationPreferences }>(
    `${base(workspaceId)}/sub-agents/${agentId}/preferences`,
    {
      method: "PATCH",
      authToken: token,
      headers: ws(workspaceId),
      body: JSON.stringify(preferences),
    },
  );

/** Soft-delete a standing agent (cancels its in-flight runs server-side). */
export const deleteStandingAgent = (
  token: string,
  workspaceId: string,
  agentId: string,
) =>
  apiFetch<{ ok: boolean }>(`${base(workspaceId)}/sub-agents/${agentId}`, {
    method: "DELETE",
    authToken: token,
    headers: ws(workspaceId),
  });

// ── Approvals (sign-off queue) ──────────────────────────────────────────────
export const listApprovals = (
  token: string,
  workspaceId: string,
  agentId?: string,
) =>
  apiFetch<{ items: AgentApproval[] }>(
    `${base(workspaceId)}/agent-approvals${agentId ? `?agentId=${encodeURIComponent(agentId)}` : ""}`,
    { authToken: token, headers: ws(workspaceId) },
  );

/** Decided approvals (terminal human outcomes) for the Notifications "Past" tab, newest-first. */
export const listDecidedApprovals = (token: string, workspaceId: string) =>
  apiFetch<{ items: AgentApproval[] }>(
    `${base(workspaceId)}/agent-approvals/decided`,
    { authToken: token, headers: ws(workspaceId) },
  );

export const decideApproval = (
  token: string,
  workspaceId: string,
  id: string,
  decision: "approved" | "edited" | "rejected",
  preparedPayload?: Record<string, unknown>,
) =>
  apiFetch<AgentApproval>(`${base(workspaceId)}/agent-approvals/${id}/decide`, {
    method: "POST",
    authToken: token,
    headers: ws(workspaceId),
    body: JSON.stringify({ decision, preparedPayload }),
  });

/**
 * Rewrite a pending draft IN PLACE (Revise with AI). PATCHes the model-authored `body`; the server
 * merges it into the stored payload, protects the thread/target keys, and keeps the approval PENDING
 * — this never sends.
 */
export const updateApproval = (token: string, workspaceId: string, id: string, body: string) =>
  apiFetch<AgentApproval>(`${base(workspaceId)}/agent-approvals/${id}`, {
    method: "PATCH",
    authToken: token,
    headers: ws(workspaceId),
    body: JSON.stringify({ body }),
  });

export const undoApproval = (token: string, workspaceId: string, id: string) =>
  apiFetch<AgentApproval>(`${base(workspaceId)}/agent-approvals/${id}/undo`, {
    method: "POST",
    authToken: token,
    headers: ws(workspaceId),
  });

// ── Grants ──────────────────────────────────────────────────────────────────
export const listGrants = (
  token: string,
  workspaceId: string,
  agentId: string,
) =>
  apiFetch<{ items: AgentGrant[] }>(
    `${base(workspaceId)}/agent-grants?agentId=${encodeURIComponent(agentId)}`,
    {
      authToken: token,
      headers: ws(workspaceId),
    },
  );

export const revokeGrant = (token: string, workspaceId: string, id: string) =>
  apiFetch<{ ok: boolean }>(`${base(workspaceId)}/agent-grants/${id}`, {
    method: "DELETE",
    authToken: token,
    headers: ws(workspaceId),
  });

// ── Runs / activity / notifications (live run-status feed) ────────────────────
// The API owns only the durable facts (mirroring the `agent_run` row); the web
// client maps them onto AgentRunDetail/ActivityItem/AgentNotification.

/** One run, shaped flat by the API's `shapeRun` (the durable facts the web maps to AgentRunDetail). */
export interface ApiRunDetail {
  id: string;
  agentId: string | null;
  status: string;
  pauseState: string | null;
  trigger: string | null;
  taskDescription: string | null;
  decision: string | null;
  resultSummary: string | null;
  resultObjectId: string | null;
  /** chat_messages.id of the prose this run produced, or null (#4959). */
  resultMessageId?: string | null;
  /** The prose body the run delivered (resolved from resultMessageId), or null. */
  body?: string | null;
  error: string | null;
  moduleId: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  events: Array<{
    seq: number;
    eventType: string;
    data: Record<string, unknown>;
    createdAt: string;
  }>;
  pendingApproval: {
    id: string;
    actionType: string;
    actionClass: string;
    preparedPayload: Record<string, unknown>;
    provenance: Record<string, unknown>;
    noveltyFlags: string[];
  } | null;
  // Present on the active-runs (Running now) feed: the joined agent identity.
  agentName?: string;
  agentColor?: string;
}

/** Activity feed item — the API maps a recent terminal run to this shape directly. */
export interface ApiActivityItem {
  id: string;
  when: string;
  ts?: string | null;
  agentId: string;
  agentName: string;
  agentColor: string;
  summary: string;
  /** The prose the run delivered (briefing/triage/meeting-prep text), or null (#4959). */
  body?: string | null;
  status: string;
  trusted?: boolean;
}

/** A typed notification signal (sign_off | blocking_question | done) — matches web AgentNotification. */
export interface ApiNotification {
  id: string;
  kind: "sign_off" | "blocking_question" | "done";
  agentId: string;
  agentName: string;
  agentColor: string;
  runId: string;
  summary: string;
  when: string;
  outcome?: "success" | "failure";
  approvalId?: string;
}

/** Recent runs for one agent (feeds the per-agent Runs lens). */
export const listAgentRuns = (
  token: string,
  workspaceId: string,
  agentId: string,
) =>
  apiFetch<{ items: ApiRunDetail[] }>(
    `${base(workspaceId)}/sub-agents/${agentId}/runs`,
    { authToken: token, headers: ws(workspaceId) },
  );

/** In-flight runs across all agents (queued/running/suspended) → the "Running now" strip. */
export const listActiveRuns = (token: string, workspaceId: string) =>
  apiFetch<{ items: ApiRunDetail[] }>(
    `${base(workspaceId)}/agent-runs?active=true`,
    { authToken: token, headers: ws(workspaceId) },
  );

/** The cross-agent activity feed (recent terminal runs). */
export const listAgentActivity = (token: string, workspaceId: string) =>
  apiFetch<{ items: ApiActivityItem[] }>(`${base(workspaceId)}/agent-activity`, {
    authToken: token,
    headers: ws(workspaceId),
  });

/** The three typed notification signals (sign_off | blocking_question | done). */
export const listAgentNotifications = (token: string, workspaceId: string) =>
  apiFetch<{ items: ApiNotification[] }>(
    `${base(workspaceId)}/agent-notifications`,
    { authToken: token, headers: ws(workspaceId) },
  );
